// Who is signed in and how this environment signs people in. Mirrors the server's role model
// (src/TestBench.Server/Auth/RoleModel.cs): the client only hides what a role cannot do; the server
// enforces it. Keep both lists in step when the role model changes.

import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { apiFetch } from '@/lib/api'

export const ROLES = ['viewer', 'tester', 'editor', 'release_manager', 'admin'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'Viewer',
  tester: 'Tester',
  editor: 'Editor',
  release_manager: 'Release manager',
  admin: 'Admin',
}

export const CAPABILITIES = ['view', 'executeRuns', 'editCases', 'writeJira', 'manageRuns', 'manageUsers', 'viewAudit'] as const
export type Capability = (typeof CAPABILITIES)[number]

/** How this environment signs people in (decision D23): no sign-in locally, one shared password, or Google. */
export const AUTH_MODES = ['local', 'sharedPassword', 'google'] as const
export type AuthMode = (typeof AUTH_MODES)[number]

export const authConfigSchema = z.object({
  mode: z.enum(AUTH_MODES),
  environment: z.string(),
})
export type AuthConfig = z.infer<typeof authConfigSchema>

export const meSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  kind: z.string(),
  role: z.enum(ROLES),
  capabilities: z.array(z.enum(CAPABILITIES)),
  authMode: z.enum(AUTH_MODES),
})
export type Me = z.infer<typeof meSchema>

export function roleLabel(role: string): string {
  return (ROLE_LABELS as Record<string, string>)[role] ?? role
}

export function can(me: Me | undefined, capability: Capability): boolean {
  return me?.capabilities.includes(capability) ?? false
}

export const sessionKeys = {
  config: ['auth', 'config'] as const,
  me: ['auth', 'me'] as const,
}

export function useAuthConfig() {
  return useQuery({ queryKey: sessionKeys.config, queryFn: () => apiFetch('/auth/config', authConfigSchema), staleTime: Infinity })
}

export function useMe() {
  return useQuery({ queryKey: sessionKeys.me, queryFn: () => apiFetch('/api/me', meSchema) })
}
